/**
 * The one thing an ad's copy cannot say by itself: a number only the viewer's account knows.
 *
 * The registry is built once at module scope, so a descriptor cannot hold "ends in 7 days" —
 * the 7 is per teacher. Copy writes `{days}` instead and this fills it in, alongside
 * applyAdCopyOverride and for the same reason it runs there: the LaunchDarkly copy an operator
 * edits has to keep working, so the token has to survive a reword rather than be baked in
 * before one.
 *
 * Deliberately not a general template engine. One token, expanding to the whole phrase
 * ("in 7 days", "in 1 day", "soon") rather than a bare number — so the copy never has to spell
 * a plural, and so a descriptor pulled up by `?previewAd=` for someone with no expiry on the
 * clock still reads as a sentence instead of showing the raw token.
 */
const TOKEN = /\{days\}/g;

const FIELDS = ['headline', 'body', 'ctaLabel'];

/**
 * `ad` with `{days}` replaced, or `ad` itself — returned by identity when there is no token to
 * fill, so every campaign that doesn't use one hands consumers the same object every render.
 */
export const applyAdCopyTokens = (ad, daysLeft) => {
  if (!ad) return ad;
  const days = !daysLeft ? 'soon' : daysLeft === 1 ? 'in 1 day' : `in ${daysLeft} days`;

  const filled = {};
  for (const field of FIELDS) {
    const value = ad[field];
    if (typeof value !== 'string') continue;
    const next = value.replace(TOKEN, days);
    if (next !== value) filled[field] = next;
  }
  return Object.keys(filled).length ? { ...ad, ...filled } : ad;
};
